import { Title } from "@components/_Revamp/Typography/Title.stories";
import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import MainContainer from "@pages/EverGreen/Components/Container";
import InnerContainer from "@pages/EverGreen/Components/InnerContainer";
import { useAvivaSelector } from "@redux/hook";
import { ROUTE_PATH } from "@static/route-paths";
import { SCREEN_SIZE } from "@static/screen-size";
import { EverGreenStyle } from "@utils/Style/everGreen";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "@pages/EverGreen/Components/Button";
import { Text } from "@pages/EverGreen/Components/Text";
import Modal from "@components/_Revamp/Modal";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  getThrencheLimit,
  postPaynowEvergreen,
  postQrEvergreen,
} from "@api/index";
import { format } from "@pages/EverGreen/Helpers/helpers";
import FPInstructionCards from "./components/fast-payment-instruction-card";
import evergreenPaynowRefresh from "@assets/images/evergreen-paynow-refresh.svg";
import Spinner from "@components/_Revamp/Spinner";
import Link from "@pages/EverGreen/Components/Link";
import { updatePaymentRef } from "@redux/slices/EVER_GREEN/step4Slice";
import copyIcon from "@assets/icon/gray-copy-icon.svg";
import infoIcon from "@assets/icon/yellow-info.svg";
import downloadIcon from "@assets/icon/download-green.svg";
import TrenchesLimit from "@pages/EverGreen/Components/TrenchesLimit";

export interface IInstructionSteps {
  title: string;
  description: string;
}

interface QRResponse {
  collectionReference: string;
  requestReferenceNumber: string;
  qrCode: string;
  txnAmount: string;
}

function formatNumberDigit(num: number) {
  return num.toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });
}

const instructionSteps: IInstructionSteps[] = [
  {
    title: "Step 1: Log in",
    description: "Log in to your preferred banking account or app.",
  },
  {
    title: " Step 2: Scan or save PayNow QR",
    description:
      "Scan the above QR code or save the code then upload it from your photo library.",
  },
  {
    title: "Step 3: Verify and pay",
    description: "Verify the payment details and make your payment.",
  },
  {
    title: "Step 4: Wait for payment",
    description: `After payment, stay on this page for confirmation. Once it's confirmed, we'll bring you to the next page with more details. Or click "Check payment status" to see if your payment was successful.`,
  },
];

const formatAmountLabel = (amount: number) => {
  //convert to string and split number and keep the decimal place valu separate
  const [wholenum, decimalnum] = amount.toFixed(2).toString().split(".");

  let formatedNum = format(parseInt(wholenum), true);

  if (decimalnum !== undefined) {
    return formatedNum.concat(`.${decimalnum}`);
  } else {
    return formatedNum.concat(".00");
  }
};

const NotificationBoxInfo = () => (
  <NotificationBox className="notificationArea">
    <img src={infoIcon} alt="Information icon" />
    <div>
      <p>
        Before making payment, ensure the details below are correct and do not
        close this page until payment is confirmed.
      </p>
    </div>
  </NotificationBox>
);

const emailContent =
  "mailto:cs_life@singlife.com?subject=%3CSinglife%20Digital%20Saver%3E%20Payment%20Issue%3A%20%3CRef%20No.%3E&body=Hi%20Customer%20Service%20Team%2C%C2%A0%C2%A0%0A%0AI%20attempted%20to%20make%20an%20online%20payment%20for%20%3CSinglife%20Digital%20Saver%3E%C2%A0%20but%20encountered%20an%20issue.%20Here%20are%20the%20details%3A%0AProposal%20Number%C2%A0%3A%20%20%3CRef%20No.%3E%C2%A0%0APlan%20Name%C2%A0%3A%20%3CSinglife%20Digital%20Saver%3E%C2%A0%C2%A0%0APremium%20Amount%C2%A0%3A%20%3CPremium%20Amount%3E%C2%A0%0APremium%20Term%C2%A0%3A%20Single%20Premium%C2%A0%C2%A0%0APayment%20Method%20%3A%20FAST%20%C2%A0%C2%A0%0APlease%20investigate%20and%20advise%20on%20how%20I%20can%20proceed%20further.%0A%0ABest%20regards%0A%3C%3CYour%20Full%20Name%3E%3E%0A%3C%3CYour%20Email%20Address%3E%3E";

interface ShowModal {
  flag: boolean;
  errorMsgCode: string;
}

const pendingPaymentModal = (
  showModal: ShowModal,
  setShowModal: (modal: ShowModal) => void,
  onClickBack: () => void,
  onClickRefresh: () => void
) => {
  return (
    <Modal className={modalStyle} show={showModal.flag}>
      <h3>Awaiting Payment</h3>
      <p>Click "Refresh" to check your payment status.</p>
      <section>
        <Button
          variant="secondary"
          hoverBgColor={EverGreenStyle.colors.primary}
          hoverTextColor={EverGreenStyle.colors.white}
          label="Back"
          onClick={() => {
            setShowModal({
              errorMsgCode: "",
              flag: false,
            });
            onClickBack();
          }}
        />
        <Button
          variant="default"
          label="Refresh"
          onClick={() => {
            setShowModal({
              errorMsgCode: "",
              flag: false,
            });
            onClickRefresh();
          }}
        />
      </section>
    </Modal>
  );
};

const errorPaymentModal = (
  showModal: ShowModal,
  setShowModal: (modal: ShowModal) => void,
  onClickBack: () => void,
  onClickReload: () => void
) => {
  return (
    <Modal className={modalStyle} show={showModal.flag}>
      <h3>Something went wrong</h3>
      <p>Try reconnecting to your Wifi or internet and reload page.</p>
      <section>
        <Button
          variant="secondary"
          hoverBgColor={EverGreenStyle.colors.primary}
          hoverTextColor={EverGreenStyle.colors.white}
          label="Back"
          onClick={() => {
            setShowModal({
              errorMsgCode: "",
              flag: false,
            });
            onClickBack();
          }}
        />
        <Button
          variant="default"
          label="Reload"
          onClick={() => {
            setShowModal({
              errorMsgCode: "",
              flag: false,
            });
            onClickReload();
          }}
        />
      </section>
    </Modal>
  );
};

const failedPaymentModal = (
  showModal: ShowModal,
  setShowModal: (modal: ShowModal) => void,
  referenceNumber?: string,
  onClickBack?: () => void,
  onClickTryAnother?: () => void,
  emailParams: {
    productName: string;
    amount: string;
    customerName: string;
    customerEmail: string;
  } = {
    productName: "",
    amount: "",
    customerName: "",
    customerEmail: "",
  }
) => {
  const emailFormat = emailContent
    .replaceAll("%3CSinglife%20Digital%20Saver%3E", emailParams?.productName)
    .replaceAll("%3CRef%20No.%3E", referenceNumber || "-")
    .replaceAll("%3CPremium%20Amount%3E", emailParams.amount)
    .replaceAll(
      "%3CCustomer%E2%80%99s%20Full%20Name%3E",
      emailParams.customerName
    )
    .replaceAll(
      "%3CCustomer%E2%80%99s%20Email%20Address%3E",
      emailParams.customerEmail
    );
  return (
    <Modal className={modalStyle} show={showModal.flag}>
      <h3>Payment Unsuccessful</h3>
      <ReferenceNumber>
        Reference Number: <span>#{referenceNumber} </span>
        <img
          onClick={() => {
            navigator?.clipboard?.writeText(referenceNumber || "-");
          }}
          src={copyIcon}
          alt="copy reference number"
        />
      </ReferenceNumber>
      <p>
        We couldn't process your payment. Don't worry, you can{" "}
        <a className="email" href={emailFormat}>
          email
        </a>{" "}
        or{" "}
        <a className="email" href="tel:+65 6827 9933">
          call
        </a>{" "}
        us with the reference number above, or try another payment mode.
      </p>
      <section>
        <Button
          variant="secondary"
          hoverBgColor={EverGreenStyle.colors.primary}
          hoverTextColor={EverGreenStyle.colors.white}
          label="Back"
          onClick={() => {
            setShowModal({
              errorMsgCode: "",
              flag: false,
            });
            if (onClickBack) onClickBack();
          }}
        />
        <Button
          variant="default"
          label="Try another payment mode"
          onClick={() => {
            setShowModal({
              errorMsgCode: "",
              flag: false,
            });
            if (onClickTryAnother) onClickTryAnother();
          }}
        />
      </section>
    </Modal>
  );
};

function PayNow() {
  const [showModal, setShowModal] = useState({ flag: false, errorMsgCode: "" });
  const [qrResponse, setQrResponse] = useState<QRResponse>();
  const history = useHistory();
  const everGreenStep1 = useAvivaSelector((state) => state.everGreenStep1);
  const everGreenStep2 = useAvivaSelector((state) => state.everGreenStep2);
  const refreshTimer = useRef<NodeJS.Timeout>();
  const [refreshTime, setRefreshTime] = useState<number>(600);
  const statusTimer = useRef<NodeJS.Timeout>();
  const [loading, setLoading] = useState<boolean>(false);
  const reloadCount = useRef<number>(0);
  const dispatch = useDispatch();
  const transactionId = useAvivaSelector(
    (state) => state.everGreenStep1.transactionId
  );
  const refreshClickCount = useRef<number>(0);
  const exceptionCount = useRef<number>(0);
  const [enableCheckStatus, setEnableCheckStatus] = useState<boolean>(false);
  const [enableStatusTimer, setEnableStatusTimer] = useState<boolean>(true);
  const [isLimit, setIsLimit] = useState<boolean>();
  const arcCode = useAvivaSelector(state=>state.everGreenStep3.arcCode)

  const trackingCodeJson = {
    ecid:"",pid:"",arc:arcCode,formType:""}

  const loadQRCode = useCallback(() => {
    setLoading(true);
    postQrEvergreen({
      transactionId: everGreenStep2.myinfoTransactionId || transactionId || "",
      customerId: everGreenStep2.myInfo.customerId as number,
      trackingCodeJson,
      productName: "DIGITALSAVER",
      
    })
      .then((res: { data: QRResponse }) => {
        setRefreshTime(600);
        setQrResponse(res.data);
      })
      .catch(() => {
        setShowModal({
          errorMsgCode: "error",
          flag: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    everGreenStep2.myinfoTransactionId,
    everGreenStep2.myInfo.customerId,
    transactionId,
  ]);

  const loadStatus = useCallback(async () => {
    return postPaynowEvergreen({
      collectionTxnRefNo: qrResponse?.collectionReference || "",
      transactionId: everGreenStep2.myinfoTransactionId || transactionId || "",
      productName: "DIGITALSAVER",
      customerId: everGreenStep2.myInfo.customerId as number,
      requestReferenceNumber: qrResponse?.requestReferenceNumber || "",
    });
  }, [
    qrResponse?.collectionReference,
    qrResponse?.requestReferenceNumber,
    everGreenStep2.myinfoTransactionId,
    everGreenStep2.myInfo.customerId,
    transactionId,
  ]);

  const onClickLoadStatus = useCallback(async () => {
    setLoading(true);
    const { data: limit } = await getThrencheLimit(
      everGreenStep1.quotation.investingAmount as number,
      undefined
    );
    if (limit.trancheLimit) {
      setIsLimit(true);
      setShowModal({
        errorMsgCode: "limit",
        flag: true,
      });
      return;
    } else {
      setIsLimit(false);
      setShowModal({
        errorMsgCode: "",
        flag: false,
      });
    }
    try {
      const res = await loadStatus();
      if (res?.data?.vendorTxnStatus?.toLowerCase() === "success") {
        dispatch(
          updatePaymentRef({
            paymentRef: res?.data?.transactionId || "",
            policyNumber: res?.data?.policyNumber || "",
          })
        );
        history.push(ROUTE_PATH.EVER_GREEN_THANK_YOU);
      } else {
        setShowModal({
          errorMsgCode: "pending",
          flag: true,
        });
      }
    } catch {
      setShowModal({
        errorMsgCode: "pending",
        flag: true,
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch, everGreenStep1.quotation.investingAmount, history, loadStatus]);

  useEffect(() => {
    getThrencheLimit(
      everGreenStep1.quotation.investingAmount as number,
      undefined
    ).then((res) => {
      if (res.data.trancheLimit) {
        setIsLimit(true);
        setShowModal({
          errorMsgCode: "limit",
          flag: true,
        });
      } else {
        loadQRCode();
      }
    });
  }, [everGreenStep1.quotation.investingAmount, loadQRCode]);

  useEffect(() => {
    refreshTimer.current = setInterval(() => {
      setRefreshTime((previousTime) => {
        if (previousTime === 0) {
          return 0;
        } else {
          return previousTime - 1;
        }
      });
    }, 1000);

    return () => {
      clearInterval(refreshTimer.current);
    };
  }, []);

  useEffect(() => {
    const intervalTime = 5000;
    const initloadingTimer = setTimeout(() => {
      statusTimer.current = setInterval(() => {
        if (!enableStatusTimer) {
          clearInterval(statusTimer.current);
          return;
        } else {
          if (isLimit) {
            return;
          }
          reloadCount.current++;
          loadStatus()
            .then((res) => {
              if (res?.data?.vendorTxnStatus?.toLowerCase() === "success") {
                dispatch(
                  updatePaymentRef({
                    paymentRef: res?.data?.transactionId || "",
                    policyNumber: res?.data?.policyNumber || "",
                  })
                );
                history.push(ROUTE_PATH.EVER_GREEN_THANK_YOU);
              } else {
                if (res?.data?.vendorTxnStatus?.toLowerCase() === "failed") {
                  if (exceptionCount.current === 3) {
                    setShowModal({
                      errorMsgCode: "failed",
                      flag: true,
                    });
                    clearInterval(statusTimer.current);
                    return;
                  }
                  exceptionCount.current++;
                }
              }
            })
            .catch(() => {
              if (exceptionCount.current === 3) {
                setShowModal({
                  errorMsgCode: "failed",
                  flag: true,
                });
                clearInterval(statusTimer.current);
                return;
              }
              exceptionCount.current++;
            });
        }
      }, intervalTime);
    }, 10 * 1000);
    return () => {
      clearTimeout(initloadingTimer);
      if (statusTimer.current) clearInterval(statusTimer.current);
    };
  }, [dispatch, enableStatusTimer, history, isLimit, loadStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableCheckStatus(true);
    }, 60 * 1000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableStatusTimer(false);
    }, 900 * 1000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <Spinner show={loading} />
      <MainContainer backButtonPath={ROUTE_PATH.EVER_GREEN_PAYMENT} step={4}>
        <Container className={marginBottom}>
          <Title variant="title" align="center">
            Scan or save PayNow QR
          </Title>
          <Title variant="description" align="center">
            Log in to your banking account or app, then scan or upload QR from
            your photo gallery.
          </Title>
        </Container>
        <Container style={{ display: "block" }}>
          <InnerContainer>
            <NotificationBoxInfo />
            <SpiltUpContainer mariginTop="24px">
              <div>
                <FieldLable>Recipient Name</FieldLable>
                <FieldValue>
                  {everGreenStep1.staticData.singlifePayeeAccount}
                </FieldValue>
              </div>
              <div>
                <FieldLable>Reference No.</FieldLable>
                <FieldValue>{qrResponse?.requestReferenceNumber}</FieldValue>
              </div>
              <div>
                <FieldLable>Payment Amount</FieldLable>
                <FieldValue>
                  $
                  {formatAmountLabel(
                    parseFloat(qrResponse?.txnAmount || "0")
                  ) || "-"}
                </FieldValue>
              </div>
            </SpiltUpContainer>
            <div className={qrContainerStyle}>
              {refreshTime === 0 ? (
                <div
                  className={qrRefreshStyle}
                  onClick={() => {
                    loadQRCode();
                  }}
                >
                  <span>Refresh code</span>
                  <img src={evergreenPaynowRefresh} alt="refresh-img" />
                </div>
              ) : null}
              <img
                src={`data:image/jpeg;base64,${qrResponse?.qrCode || ""}`}
                alt="Paynow QR code"
              />
            </div>
            <Link
              theme={EverGreenStyle}
              text="Save QR code"
              target="_blank"
              rel="noopener noreferrer"
              url={`data:image/jpeg;base64,${qrResponse?.qrCode || ""}`}
              download="qrcode.jpg"
              hasDownload
              className={saveBtnStyle}
              icon={downloadIcon}
              slotIcon="start"
            />

            {refreshTime > 0 ? (
              <Text
                variant="base"
                fontWeight={600}
                align="center"
                color="#000000"
              >
                Expiring in {formatNumberDigit(Math.floor(refreshTime / 60))}:
                {formatNumberDigit(refreshTime % 60)} mins
              </Text>
            ) : null}
          </InnerContainer>
        </Container>
        {/* <Checkbox className={checkBoxStyle} name="tnc" color='#434343' checked={true} label='By checking this box, I confirm that I have read and understood the above.' /> */}

        <div className={instructionStyle}>How to pay with PayNow QR</div>
        <Container>
          {instructionSteps.map((val, i) => (
            <FPInstructionCards
              key={`ins-${i}`}
              content={val}
              titleColor="#000000"
            />
          ))}
        </Container>

        <Button
          label={"Check payment status"}
          width="308px"
          margin={"64px auto"}
          onClick={() => {
            onClickLoadStatus();
          }}
          disabled={!enableCheckStatus}
        />
      </MainContainer>
      {showModal?.errorMsgCode?.toLowerCase() === "pending" &&
        pendingPaymentModal(
          showModal,
          setShowModal,
          () => {
            loadQRCode();
          },
          () => {
            if (refreshClickCount.current === 5) {
              setShowModal({
                errorMsgCode: "failed",
                flag: true,
              });
              return;
            } else {
              refreshClickCount.current++;
              onClickLoadStatus();
            }
          }
        )}
      {showModal?.errorMsgCode?.toLowerCase() === "error" &&
        errorPaymentModal(
          showModal,
          setShowModal,
          () => {
            history.push(ROUTE_PATH.EVER_GREEN_PAYMENT);
          },
          () => {
            window.location.reload();
          }
        )}
      {showModal?.errorMsgCode?.toLowerCase() === "failed" &&
        failedPaymentModal(
          showModal,
          setShowModal,
          qrResponse?.requestReferenceNumber,
          () => {
            loadQRCode();
          },
          () => {
            history.push(ROUTE_PATH.EVER_GREEN_PAYMENT);
          },
          {
            productName: everGreenStep1.staticData.productName,
            customerName: everGreenStep2.myInfo.fullName || "",
            customerEmail: everGreenStep2.myInfo.emailId || "",
            amount:
              "S$" +
              formatAmountLabel(parseFloat(qrResponse?.txnAmount || "0")),
          }
        )}
      {showModal?.errorMsgCode?.toLowerCase() === "limit" && (
        <Modal show>
          <TrenchesLimit
            closeModal={() => {
              setShowModal({
                errorMsgCode: "",
                flag: false,
              });
              window.location.replace(ROUTE_PATH.EVER_GREEN_LANDING_PAGE);
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default PayNow;

export const Container = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`;

const qrContainerStyle = css`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  img {
    width: 210px;
    height: 210px;
  }
`;

const qrRefreshStyle = css`
  color: white;
  background: rgba(0, 0, 0, 0.65);
  position: absolute;
  width: 210px;
  height: 210px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  img {
    width: 64px;
    height: 64px;
  }
  span {
    font-weight: 500;
  }
`;

const instructionStyle = css`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  line-height: 22.4px;
  letter-spacing: 0.24px;
  flex-grow: 1;
  gap: 20px;
  margin-top: 72px;
  margin-bottom: 24px;
  &::after,
  &::before {
    content: "";
    flex-grow: 1;
    border-top: dotted 2px black;
  }
`;

const modalStyle = css`
  .modal-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    h3 {
      font-size: 28px;
      font-weight: 600;
      text-align: center;
      color: ${EverGreenStyle.colors.grey[500]};
    }
    p {
      text-align: center;
      font-size: 14px;
      font-weight: 600px;
    }
    section {
      margin-top: 24px;
      display: flex;
      gap: 24px;
      flex-direction: column;
    }

    .email {
      color: ${EverGreenStyle.colors.primary};
      font-weight: 600;
      cursor: pointer;
    }
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    .modal-body {
      p {
        text-align: center;
        font-size: 14px;
        font-weight: 600px;
      }
      section {
        margin-top: 48px;
        display: flex;
        flex-direction: row;
        gap: 24px;
      }
    }
  }
`;

const marginBottom = css`
  margin-bottom: 24px;
  p:nth-child(2) {
    font-size: 14px;
    font-weight: 600;
    color: #8a8988;
  }

  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    p:nth-child(1) {
      font-size: 28px;
      font-weight: 600;
    }
  }
`;

const SpiltUpContainer = styled.div<{ mariginTop?: string }>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 24px;
  column-gap: 20px;
  row-gap: 24px;
  margin-top: ${(props) => props.mariginTop || "60px"};
  & div {
    width: 100%;
    flex-grow: 1;
  }
  @media screen and (max-width: 767px) {
    flex-direction: column;
    margin-top: 24px;
    grid-template-columns: repeat(1, 1fr);
    & div {
      display: flex;
      flex-direction: column;
      width: auto;
      flex-grow: 1;
      column-gap: 15px;
    }
  }
`;

const FieldLable = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const FieldValue = styled.div`
  font-style: normal;
  line-height: 140%;
  color: black;
  margin-top: 16px;
  font-weight: 600;
  font-size: 14px;
  @media screen and (max-width: 767px) {
    margin-bottom: 32px;
    font-size: 14px;
  }
  @media screen and (max-width: ${SCREEN_SIZE.mobilesUp}px) {
    margin-bottom: unset;
    font-size: 14px;
  }
`;

const saveBtnStyle = css`
  display: flex;
  border: 1px solid ${EverGreenStyle.colors.primary};
  margin: 24px auto;
  padding: 14px 40px;
  border-radius: 84px;
  width: 150px;
  justify-content: center;
  u {
    text-decoration: none;
  }
`;

const ReferenceNumber = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  & > span:nth-child(2) {
    color: ${EverGreenStyle.colors.primary};
    font-size: 14px;
    font-weight: 600;
    display: flex;
    flex-direction: row;
  }
  img {
    display: flex;
    cursor: pointer;
    padding-left: 4px;
    align-items: center;
  }

  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
    & > span:nth-child(2) {
      display: flex;
      margin-left: 5px;
    }
  }
`;

const NotificationBox = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: #fffce4;
  border-radius: 16px;
  padding: 16px;
  gap: 8px;
  div {
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-size: 14px;
    line-height: 22.4px;
    flex: 1;
    p {
      color: #655b00;
      font-weight: 500;

      b {
        font-weight: 600;
      }
    }
  }
  &.notificationArea {
    grid-column-start: 1;
    grid-column-end: 3;
  }
`;
